import { useEffect, useRef, useState } from 'preact/hooks'
import { Check, AlertCircle, Loader2, Lock } from 'lucide-preact'
import { slugify, fitSlug, isValidSlugFormat, SLUG_MAX_LENGTH } from '@/utils/slugify'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

type Availability = 'idle' | 'checking' | 'available' | 'taken' | 'invalid'

interface SlugInputProps {
  /** Current value */
  value: string
  /** Called with the next slug value whenever the user edits */
  onChange: (next: string) => void
  /** Label shown above the field */
  label?: string
  /** Placeholder */
  placeholder?: string
  /** Required indicator */
  required?: boolean
  /** Field error from react-hook-form (shown in red below the input) */
  error?: string
  /** Helper text shown when there is no error */
  helperText?: string
  /**
   * Optional "parent" name that the slug should auto-populate from until the
   * user has manually edited the slug. Pass `undefined` to disable auto-fill.
   */
  sourceName?: string
  /**
   * Called to check remote availability. Return { available } or null to skip.
   * Debounced internally so callers can wire it directly to an API client.
   */
  checkAvailability?: (slug: string) => Promise<{ available: boolean }> | null
  /** When true the field is read-only and renders a lock icon. */
  locked?: boolean
  /** Message shown when locked (e.g. "Unpublish first to rename"). */
  lockedMessage?: string
  /** Disabled state */
  disabled?: boolean
  /**
   * True when nothing has been persisted for this entity yet, so whatever is in the field is a
   * suggestion rather than a decision.
   *
   * Without this the field renders an auto-filled value with a green "Available" tick and looks
   * identical to a saved one, while the sign-off readiness chip correctly reports the slug as
   * missing. A tester read the tick, concluded the warning was broken, and lost an hour to it
   * (ORB-482). "Available" answers "is this name free", never "is this yours".
   */
  unsaved?: boolean
  /** Longest slug accepted. 50 by default; a web passes WEB_SLUG_MAX_LENGTH (ORB-944). */
  maxLength?: number
  /**
   * Trim the auto-filled suggestion to `maxLength` at a word boundary. Webs set it, because a title
   * can be longer than a web slug may be (ORB-944). Off by default so company and workspace slugs
   * keep today's behaviour.
   */
  fitSuggestion?: boolean
}

/**
 * Paired name/slug input used on ClientCompany and Workspace create/edit forms.
 *
 * - Auto-slugifies from `sourceName` until the user edits the slug directly.
 * - Debounces onChange propagation to the parent form (~300ms).
 * - Debounces a live availability check via `checkAvailability`.
 * - Shows format errors locally (no network hop) against the shared rule
 *   `^[a-z0-9]+(?:-[a-z0-9]+)*$`, 3 to `maxLength` chars.
 * - Can be locked (read-only) when the parent entity has an active published
 *   landing page -- renders a lock icon and a "Unpublish first" message.
 */
export function SlugInput({
  value,
  onChange,
  label = 'Slug',
  placeholder = 'my-slug',
  required = false,
  error,
  helperText,
  sourceName,
  checkAvailability,
  locked = false,
  lockedMessage,
  disabled = false,
  unsaved = false,
  maxLength = SLUG_MAX_LENGTH,
  fitSuggestion = false,
}: SlugInputProps) {
  const [localValue, setLocalValue] = useState(value)
  const [userEdited, setUserEdited] = useState(Boolean(value))
  const [availability, setAvailability] = useState<Availability>('idle')
  const checkTimer = useRef<number | null>(null)
  const lastEmitted = useRef(value)
  const checkAvailabilityRef = useRef(checkAvailability)
  checkAvailabilityRef.current = checkAvailability

  const debouncedLocal = useDebouncedValue(localValue, 300)

  // Propagate debounced value to parent.
  useEffect(() => {
    if (debouncedLocal !== lastEmitted.current) {
      lastEmitted.current = debouncedLocal
      onChange(debouncedLocal)
    }
  }, [debouncedLocal])

  // Sync external value changes (form reset, programmatic setValue) into local state.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      setLocalValue(value)
      lastEmitted.current = value
    }
  }, [value])

  // Auto-populate from sourceName until user edits the slug directly.
  // Bypasses debounce -- auto-populate should feel immediate.
  useEffect(() => {
    if (userEdited || locked) return
    if (!sourceName) return
    const next = fitSuggestion ? fitSlug(slugify(sourceName), maxLength) : slugify(sourceName)
    if (next !== localValue) {
      setLocalValue(next)
      lastEmitted.current = next
      onChange(next)
    }
  }, [sourceName, userEdited, locked, fitSuggestion, maxLength])

  // Debounced availability check -- watches localValue for responsive feedback.
  useEffect(() => {
    if (checkTimer.current) {
      window.clearTimeout(checkTimer.current)
      checkTimer.current = null
    }
    if (locked || disabled) {
      setAvailability('idle')
      return
    }
    if (!localValue) {
      setAvailability('idle')
      return
    }
    if (!isValidSlugFormat(localValue, maxLength)) {
      setAvailability('invalid')
      return
    }
    if (!checkAvailabilityRef.current) {
      setAvailability('idle')
      return
    }
    setAvailability('checking')
    checkTimer.current = window.setTimeout(async () => {
      try {
        const result = await checkAvailabilityRef.current!(localValue)
        if (!result) {
          setAvailability('idle')
          return
        }
        setAvailability(result.available ? 'available' : 'taken')
      } catch {
        setAvailability('idle')
      }
    }, 350) as unknown as number
    return () => {
      if (checkTimer.current) window.clearTimeout(checkTimer.current)
    }
  }, [localValue, locked, disabled, maxLength])

  const handleInput = (e: Event) => {
    const raw = (e.target as HTMLInputElement).value
    // Normalize as the user types: lowercase, replace spaces with dashes.
    const next = raw.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    setUserEdited(true)
    setLocalValue(next)
  }

  // Flush local value to parent on blur so the form always has the latest
  // value before a submit handler runs.
  const handleBlur = () => {
    if (localValue !== value) {
      lastEmitted.current = localValue
      onChange(localValue)
    }
  }

  // A tick on an unpersisted value is the whole bug — it reads as "saved" when it only ever meant
  // "free". Suppressed while unsaved; the error and taken/invalid states still show, because those
  // are true regardless of whether anything has been stored.
  const showAvailableTick = availability === 'available' && !unsaved

  const statusIcon = () => {
    if (locked) return <Lock className="w-4 h-4 text-slate-400" />
    if (availability === 'checking') return <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
    if (showAvailableTick) return <Check className="w-4 h-4 text-green-600" />
    if (availability === 'taken' || availability === 'invalid') return <AlertCircle className="w-4 h-4 text-red-600" />
    return null
  }

  const statusMessage = () => {
    if (locked) return lockedMessage || 'Slug is locked while this entity has a published landing page.'
    if (error) return error
    if (availability === 'invalid') return `Slug must be 3-${maxLength} lowercase letters, numbers, or hyphens.`
    if (availability === 'taken') return 'That slug is already taken.'
    if (unsaved) return helperText
    if (availability === 'available') return 'Available'
    return helperText
  }

  const messageClass =
    error || availability === 'taken' || availability === 'invalid'
      ? 'text-red-600 dark:text-red-400'
      : showAvailableTick
        ? 'text-green-600 dark:text-green-400'
        : 'text-slate-500 dark:text-slate-400'

  const showUnsavedNotice = unsaved && !locked

  return (
    <div>
      <label className="flex items-center justify-between gap-3 text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        <span>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
        {showUnsavedNotice && (
          <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Not set yet</span>
        )}
      </label>
      <div className="relative">
        <input
          type="text"
          value={localValue}
          readOnly={locked}
          disabled={disabled}
          onInput={handleInput}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`w-full px-3 py-2 pr-9 border rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${
            error || availability === 'taken' || availability === 'invalid'
              ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
              : showUnsavedNotice
                ? 'border-dashed border-slate-400 dark:border-slate-500 focus:ring-primary-500'
                : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500'
          } ${locked
              ? 'bg-slate-100 dark:bg-slate-800 cursor-not-allowed'
              : showUnsavedNotice
                ? 'bg-slate-50 dark:bg-slate-800/60'
                : 'bg-white dark:bg-slate-700'}`}
        />
        <div className="absolute inset-y-0 right-2 flex items-center">{statusIcon()}</div>
      </div>
      {statusMessage() && <p className={`mt-1 text-xs ${messageClass}`}>{statusMessage()}</p>}
    </div>
  )
}
